/**
 * Findings-ledger display utilities (NDA lifecycle rework PR-5c).
 *
 * The ledger stores a `concern` paragraph + a `recommendation` (which may be a
 * single paragraph or a newline/bullet list). The modal renders them merged as
 * "Concern & Recommendation" on the row: the concern paragraph followed by a
 * bulleted list of the recommendation(s). Expanded rows show the anchor→
 * replacement as an inline word-level diff.
 */

export interface SplitRecommendation {
  concern: string;
  bullets: string[];
}

const _BULLET_PREFIX = /^\s*([-•*‣·]|\d+[.)])\s*/;

/**
 * Split a finding's concern + recommendation into the row's concern paragraph
 * and its recommendation bullets. Each non-empty line of the recommendation
 * becomes a bullet (leading bullet/number markers stripped); a single-paragraph
 * recommendation yields exactly one bullet.
 */
export function splitRecommendation(
  concern: string | null | undefined,
  recommendation: string | null | undefined,
): SplitRecommendation {
  const concernText = (concern ?? '').trim();
  const raw = (recommendation ?? '').trim();
  const bullets = raw
    ? raw
        .split(/\r?\n/)
        .map((l) => l.replace(_BULLET_PREFIX, '').trim())
        .filter(Boolean)
    : [];
  return { concern: concernText, bullets };
}

export type DiffOp = 'same' | 'add' | 'del';
export interface DiffToken {
  text: string;
  type: DiffOp;
}

// Tokenize into words + the whitespace between them, so a rebuild is lossless
// and the diff reads at word granularity (not character noise).
function _tokenize(s: string): string[] {
  return (s || '').match(/\S+|\s+/g) ?? [];
}

/**
 * Word-level old→new diff (LCS). Returns tokens tagged `del` (in old only),
 * `add` (in new only), or `same`. Rebuilding the `del`+`same` tokens yields the
 * old string; `add`+`same` yields the new string.
 */
export function wordDiff(oldText: string | null | undefined, newText: string | null | undefined): DiffToken[] {
  const a = _tokenize(oldText ?? '');
  const b = _tokenize(newText ?? '');
  const n = a.length;
  const m = b.length;
  // LCS length table.
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffToken[] = [];
  const push = (text: string, type: DiffOp) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ text, type });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push(a[i], 'same');
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push(a[i], 'del');
      i++;
    } else {
      push(b[j], 'add');
      j++;
    }
  }
  while (i < n) push(a[i++], 'del');
  while (j < m) push(b[j++], 'add');
  return out;
}
